import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Auth } from '@angular/fire/auth';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';

/**
 * Registers Capacitor lifecycle hooks at startup (native only):
 *   - Session expiry: forces token refresh on foreground; signs out on expired session.
 *   - Deep linking: handles cold-start URLs, background appUrlOpen events, and
 *     push notification taps — all routed to /requests/:requestId.
 */
@Injectable({ providedIn: 'root' })
export class MobileAppLifecycleService {
    private readonly _auth   = inject(Auth);
    private readonly _router = inject(Router);

    constructor() {
        if (!Capacitor.isNativePlatform()) return;

        // ── Session expiry ──────────────────────────────────────────────────────
        App.addListener('appStateChange', async ({ isActive }) => {
            if (!isActive) return;

            const user = this._auth.currentUser;
            if (!user) return;

            try {
                await user.getIdToken(/* forceRefresh */ true);
            } catch {
                await this._auth.signOut().catch(() => {});
                this._router.navigate(['/sign-in']);
            }
        });

        // ── Deep linking — cold start ───────────────────────────────────────────
        App.getLaunchUrl().then(({ url }) => {
            if (url) this._handleAppUrl(url);
        }).catch(() => {});

        // ── Deep linking — background URL open ─────────────────────────────────
        App.addListener('appUrlOpen', ({ url }) => {
            this._handleAppUrl(url);
        });

        // ── Push notification tap ───────────────────────────────────────────────
        PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
            const requestId = action.notification.data?.requestId as string | undefined;
            if (requestId) this._navigateToRequest(requestId);
        });
    }

    // ── Private ────────────────────────────────────────────────────────────────

    private _handleAppUrl(url: string): void {
        try {
            const path = new URL(url).pathname;
            const match = path.match(/\/requests\/([^/]+)/);
            if (match?.[1]) this._navigateToRequest(match[1]);
        } catch {
            // Malformed URL — silently ignore.
        }
    }

    private _navigateToRequest(requestId: string): void {
        this._router.navigate(['/requests', requestId]);
    }
}
